import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IColumnsMap } from 'libs/design-system/src/types/data_table';
import { DataTable, Button } from '@unified-checkout/design-system';
import { Plus, Pen } from '@unified-checkout/design-system/icons/index';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { getBranches } from '@app/dashboard/services/inner_layout/branches';
import type { BranchData } from '@app/dashboard/types/innerLayout/branches';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import { AddBranchModal } from './AddBranchModal';
import { UpdateBranchModal } from './UpdateBranchModal';
import styles from './branches.module.scss';

const TABLE_ID = 'branches_table';

export const Branches = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<BranchData | null>(null);

  const isStaff = getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;
  const isMerchant = getProfileType() === PROFILE.TYPE.MERCHANT;
  const canUpdateBranch = isStaff ? isAccessible(PERMISSIONS.CAN_UPDATE_TERMINAL) : isMerchant;
  const canCreateTerminalBranch = isStaff ? isAccessible(PERMISSIONS.CAN_CREATE_TERMINAL_BRANCH) : isMerchant;

  const handleOpenUpdateModal = (row: BranchData) => {
    setSelectedBranch(row);
  };

  const handleCloseUpdateModal = () => {
    setSelectedBranch(null);
  };

  const columnsMap: IColumnsMap = useMemo(
    () => [
      {
        id: 'branch_name',
        name: t('innerLayout.branchesPage.table.headers.branchName'),
        type: 'text',
        key: 'branch_name',
      },
      {
        id: 'branch_id',
        name: t('innerLayout.branchesPage.table.headers.branchId'),
        type: 'text',
        key: 'branch_id',
      },
      {
        id: 'city',
        name: t('innerLayout.branchesPage.table.headers.city'),
        type: 'text',
        key: 'city',
      },
      {
        id: 'address',
        name: t('innerLayout.branchesPage.table.headers.address'),
        type: 'text',
        key: 'address',
      },
      ...(canUpdateBranch
        ? [
            {
              id: 'actions',
              name: t('innerLayout.branchesPage.table.headers.actions'),
              type: 'action_btns' as const,
              key: 'branch_id',
              actionBtnsProps: [
                {
                  leadingIcon: Pen,
                  text: t('innerLayout.update'),
                  onClickHandler: handleOpenUpdateModal,
                  variant: 'link' as const,
                  color: 'primary' as const,
                },
              ],
            },
          ]
        : []),
    ],
    [t, canUpdateBranch]
  );

  const queryKeys = useMemo(() => [QUERY_KEYS.GET_BRANCHES, isLive], [isLive]);
  const queryFunctionParams = useMemo(() => ({ is_live: isLive }), [isLive]);
  const resetFiltersParams = useMemo(() => [isLive], [isLive]);

  return (
    <div className={styles['branches-wrapper']}>
      <div className={styles['header']}>
        <div className={styles['header__title']}>
          {t('innerLayout.branchesPage.title')}
        </div>
        {canCreateTerminalBranch && (
          <Button
            size="md"
            color="primary"
            variant="contained"
            leadingIcon={Plus}
            text={t('innerLayout.branchesPage.addButton')}
            onClickHandler={() => setAddModalOpen(true)}
          />
        )}
      </div>

      <DataTable
        lang={lang}
        tableBody={{
          lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile,
        }}
        queries={{
          queryKeys,
          queryFunction: getBranches,
          queryFunctionParams,
          resetFiltersParams,
        }}
        emptyState={{
          text: t('innerLayout.branchesPage.table.body.noBranchesYet'),
          description: t('innerLayout.branchesPage.table.body.noBranchesHint'),
        }}
      />

      <AddBranchModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        isMobile={isMobile}
      />

      <UpdateBranchModal
        open={!!selectedBranch}
        onClose={handleCloseUpdateModal}
        branch={selectedBranch}
        isMobile={isMobile}
      />
    </div>
  );
};
